import { useState, useMemo } from 'react';
import { Button, Dimmer, Form, Loader, Modal } from 'semantic-ui-react';

import AppConfig from '../../AppConfig';
import { useAppContext } from '../../Contexts/AppContext';
import * as Messages from '../../Resources/Messages';
import ErrorMessage from '../ErrorMessage';

function normalizeMasterCode(value) {
    return String(value ?? '')
        .trim()
        .toUpperCase();
}

function buildStateOptionValue(countryCode, stateCode) {
    const normalizedCountryCode = normalizeMasterCode(countryCode);
    const normalizedStateCode = normalizeMasterCode(stateCode);
    if (!normalizedCountryCode || !normalizedStateCode) {
        return String(stateCode ?? '').trim();
    }
    return `${normalizedCountryCode}_${normalizedStateCode}`;
}

function extractStateCodeFromOptionValue(optionValue) {
    const raw = String(optionValue ?? '').trim();
    if (!raw) return '';

    const separatorIndex = raw.indexOf('_');
    if (separatorIndex === -1) return raw;
    return raw.slice(separatorIndex + 1);
}

function generateInitialState(props, Reducer) {
    const addressBook = Reducer.customerAddressBook.find(
        (x) => x.Id == props.id
    );
    return {
        addressLine1: props.edit ? addressBook.AddressLineOne : '',
        addressLine2: props.edit ? addressBook.AddressLineTwo : '',
        addressLine3: props.edit ? addressBook.AddressLineThree : '',
        city: props.edit ? addressBook.City : '',
        state: props.edit ? addressBook.State : '',
        zip: props.edit ? addressBook.Zip : '',
        country: props.edit ? addressBook.Country : 'US',
        telephone: props.edit ? addressBook.Telephone : '',
        fax: props.edit ? addressBook.Fax : '',

        validationErrors: {},
        loading: false,
        loadingError: false,
        errorMessage: '',
    };
}

export default function AddressEditModal(props) {
    const { Reducer, Actions } = useAppContext();
    const [state, setState] = useState(() =>
        generateInitialState(props, Reducer)
    );
    const [customStateOptions, setCustomStateOptions] = useState([]);
    const [customCountryOptions, setCustomCountryOptions] = useState([]);

    const stateOptions = useMemo(() => {
        const currentCountryCode = normalizeMasterCode(state.country);

        const masterStateOptions = (Reducer.stateMaster || [])
            .filter(
                (x) =>
                    !currentCountryCode ||
                    normalizeMasterCode(x.CountryCode) === currentCountryCode
            )
            .map((x) => {
                const optionValue = buildStateOptionValue(
                    x.CountryCode,
                    x.Code
                );
                return {
                    key: optionValue,
                    value: optionValue,
                    text: `${x.Code} - ${x.Name}`,
                    stateCode: String(x.Code ?? '').trim(),
                };
            });

        const currentStateVal = String(state.state ?? '').trim();
        const selectedMasterStateOption = masterStateOptions.find(
            (x) => String(x.stateCode ?? '').trim() === currentStateVal
        );
        const hasMasterStateOption = !!selectedMasterStateOption;

        return [
            ...masterStateOptions,
            ...customStateOptions,
            ...(currentStateVal &&
            !hasMasterStateOption &&
            !customStateOptions.some((x) => x.value === currentStateVal)
                ? [
                      {
                          key: currentStateVal,
                          value: currentStateVal,
                          text: currentStateVal,
                      },
                  ]
                : []),
        ];
    }, [Reducer.stateMaster, customStateOptions, state.state, state.country]);

    const selectedMasterStateOption = useMemo(() => {
        const currentStateVal = String(state.state ?? '').trim();
        return stateOptions.find(
            (x) => String(x.stateCode ?? '').trim() === currentStateVal
        );
    }, [stateOptions, state.state]);

    const currentStateDropdownValue =
        selectedMasterStateOption?.value ?? String(state.state ?? '').trim();
    const countryOptions = useMemo(() => {
        const masterOptions = Reducer.countryMaster.map((x) => ({
            key: x.Code,
            value: x.Code,
            text: `${x.Code} - ${x.Name}`,
        }));
        const merged = [...masterOptions];
        customCountryOptions.forEach((opt) => {
            if (!merged.some((m) => m.value === opt.value)) {
                merged.push(opt);
            }
        });
        // master/custom のどちらにも無ければ1件追加する
        const currentCountryVal = state.country?.trim();
        if (
            currentCountryVal &&
            !merged.some((m) => m.value === currentCountryVal)
        ) {
            merged.push({
                key: currentCountryVal,
                value: currentCountryVal,
                text: currentCountryVal,
            });
        }
        return merged;
    }, [Reducer.countryMaster, customCountryOptions, state.country]);

    // fetch
    //#region
    function addressInsert(saveData) {
        setState((prev) => ({ ...prev, loading: true }));
        fetch(AppConfig.ApiUrlUserSetting + '/addressBook', {
            mode: 'cors',
            method: 'post',
            credentials: 'include',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                csrftoken: JSON.stringify(Reducer.csrftoken),
            },
            body: JSON.stringify(saveData),
        })
            .then((response) => {
                if (!response.ok) {
                    return response.json().then(function (err) {
                        throw new Error(err.Message);
                    });
                }
                return response;
            })
            .then((response) => response.json())
            .then((response) => {
                Actions.setCustomerAddressBook(response);
                setState((prev) => ({ ...prev, loading: false }));
                props.setSuccessFlag(true, false);
                props.onModalClose();
            })
            .catch((e) => {
                setState((prev) => ({
                    ...prev,
                    loading: false,
                    loadingError: true,
                    errorMessage: e.message,
                }));
            });
    }

    function addressUpdate(saveData) {
        setState((prev) => ({ ...prev, loading: true }));
        fetch(AppConfig.ApiUrlUserSetting + '/addressBook', {
            mode: 'cors',
            method: 'put',
            credentials: 'include',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                csrftoken: JSON.stringify(Reducer.csrftoken),
            },
            body: JSON.stringify(saveData),
        })
            .then((response) => {
                if (!response.ok) {
                    return response.json().then(function (err) {
                        throw new Error(err.Message);
                    });
                }
                return response;
            })
            .then((response) => response.json())
            .then((response) => {
                Actions.setCustomerAddressBook(response);
                setState((prev) => ({ ...prev, loading: false }));
                props.setSuccessFlag(false, true);
                props.onModalClose();
            })
            .catch((e) => {
                setState((prev) => ({
                    ...prev,
                    loading: false,
                    loadingError: true,
                    errorMessage: e.message,
                }));
            });
    }
    //#endregion

    function onValueChange(evn, data) {
        setState((prev) => {
            const nextValue =
                data.stateName === 'state'
                    ? extractStateCodeFromOptionValue(data.value)
                    : data.value;

            const nextState = { ...prev, [data.stateName]: nextValue };

            if (data.stateName === 'country') {
                const currentStateVal = String(prev.state ?? '').trim();
                const nextCountryCode = normalizeMasterCode(data.value);

                if (currentStateVal) {
                    const matched = (Reducer.stateMaster || []).some(
                        (x) =>
                            normalizeMasterCode(x.Code) ===
                                normalizeMasterCode(currentStateVal) &&
                            normalizeMasterCode(x.CountryCode) ===
                                nextCountryCode
                    );

                    if (!matched) {
                        nextState.state = '';
                    }
                }
            }

            return nextState;
        });
    }

    function onStateAddItem(evn, data) {
        const entered = String(data.value ?? '').trim();
        if (!entered) return;

        setCustomStateOptions((prev) => {
            if (prev.some((option) => option.value === entered)) {
                return prev;
            }
            return [
                ...prev,
                {
                    key: entered,
                    value: entered,
                    text: entered,
                },
            ];
        });
    }

    function onCountryAddItem(evn, data) {
        setCustomCountryOptions((prev) => {
            if (prev.some((option) => option.value === data.value)) {
                return prev;
            }
            return [
                ...prev,
                { key: data.value, value: data.value, text: data.value },
            ];
        });
    }

    function onSubmitClick() {
        let saveData = createSaveData();
        let validationErrors = validationCheck(saveData);
        setState((prev) => ({
            ...prev,
            ...saveData,
            validationErrors,
            loadingError: false,
            errorMessage: '',
        }));

        if (!Object.keys(validationErrors).length) {
            if (props.new) {
                addressInsert(saveData);
            } else if (props.edit) {
                addressUpdate(saveData);
            }
        }
    }

    function createSaveData() {
        return {
            id: props.id,
            addressLine1: state.addressLine1.trim(),
            addressLine2: state.addressLine2.trim(),
            addressLine3: state.addressLine3.trim(),
            city: state.city.trim(),
            state: state.state.trim(),
            zip: state.zip.trim(),
            country: state.country.trim(),
            telephone: state.telephone.trim(),
            fax: state.fax.trim(),
        };
    }

    function validationCheck(saveData) {
        let errors = {};

        if (!saveData.addressLine1) {
            errors.addressLine =
                Messages.REPAIRREQUEST_MSG_VALIDATEMESSAGE_ADRESSNULL;
        } else if (
            saveData.addressLine1.length > 100 ||
            saveData.addressLine2?.length > 100 ||
            saveData.addressLine3?.length > 100
        ) {
            errors.addressLine =
                Messages.REPAIRREQUEST_MSG_VALIDATEMESSAGE_ADRESSLENGTH;
        }
        if (!saveData.city) {
            errors.city = Messages.REPAIRREQUEST_MSG_VALIDATEMESSAGE_CITYNULL;
        } else if (saveData.city.length > 100) {
            errors.city = Messages.REPAIRREQUEST_MSG_VALIDATEMESSAGE_CITYLENGTH;
        }
        if (!saveData.state) {
            errors.state =
                Messages.REPAIRREQUEST_MSG_VALIDATEMESSAGE_STATESELECTORENTER;
        } else if (saveData.state.length > 100) {
            errors.state =
                Messages.REPAIRREQUEST_MSG_VALIDATEMESSAGE_STATELENGTH;
        }
        if (!saveData.zip) {
            errors.zip = Messages.REPAIRREQUEST_MSG_VALIDATEMESSAGE_ZIPNULL;
        } else if (saveData.zip.length > 100) {
            errors.zip = Messages.REPAIRREQUEST_MSG_VALIDATEMESSAGE_ZIPLENGTH;
        }
        if (!saveData.country) {
            errors.country =
                Messages.REPAIRREQUEST_MSG_VALIDATEMESSAGE_COUNTRYNULL;
        } else if (saveData.country.length > 100) {
            errors.country =
                Messages.REPAIRREQUEST_MSG_VALIDATEMESSAGE_COUNTRYLENGTH;
        }
        if (!saveData.telephone) {
            errors.telephone =
                Messages.REPAIRREQUEST_MSG_VALIDATEMESSAGE_TELEPHONENULL;
        } else if (saveData.telephone.length > 100) {
            errors.telephone =
                Messages.REPAIRREQUEST_MSG_VALIDATEMESSAGE_TELEPHONELENGTH;
        }
        if (saveData.fax.length > 100) {
            errors.fax = Messages.REPAIRREQUEST_MSG_VALIDATEMESSAGE_FAXLENGTH;
        }

        if (Object.keys(errors).length == 0) {
            const userInformation = Reducer.userInformation;
            // CustomerMasterの住所と一致するかをチェック
            if (
                userInformation.AddressLineOne == saveData.addressLine1 &&
                userInformation.AddressLineTwo == saveData.addressLine2 &&
                userInformation.AddressLineThree == saveData.addressLine3 &&
                userInformation.City == saveData.city &&
                userInformation.State == saveData.state &&
                userInformation.Zip == saveData.zip &&
                userInformation.Country == saveData.country &&
                userInformation.Telephone == saveData.telephone &&
                userInformation.Fax == saveData.fax
            ) {
                errors.duplicate =
                    Messages.REPAIRREQUEST_MSG_VALIDATION_ADDRESS_DUPLICATE;
            }
            // AddressBookの住所と一致するかをチェック
            else if (
                Reducer.customerAddressBook.some(
                    (x) =>
                        x.Id != props.id &&
                        x.AddressLineOne == saveData.addressLine1 &&
                        x.AddressLineTwo == saveData.addressLine2 &&
                        x.AddressLineThree == saveData.addressLine3 &&
                        x.City == saveData.city &&
                        x.State == saveData.state &&
                        x.Zip == saveData.zip &&
                        x.Country == saveData.country &&
                        x.Telephone == saveData.telephone &&
                        x.Fax == saveData.fax
                )
            ) {
                errors.duplicate =
                    Messages.REPAIRREQUEST_MSG_VALIDATION_ADDRESS_DUPLICATE;
            }
        }

        return errors;
    }

    let title, submitButtonLabel;
    if (props.new) {
        title = 'Add new address';
        submitButtonLabel = 'Add';
    } else if (props.edit) {
        title = 'Edit address';
        submitButtonLabel = 'Save';
    }

    let errorMessage;
    if (Object.keys(state.validationErrors).length) {
        let content = Object.values(state.validationErrors).map((x) => (
            <>
                {x}
                <br />
            </>
        ));
        errorMessage = <ErrorMessage content={content} />;
    } else if (state.loadingError) {
        errorMessage = <ErrorMessage content={state.errorMessage} active />;
    }

    return (
        <Modal open={props.new || props.edit}>
            <Modal.Header content={title} />
            <Modal.Content>
                {errorMessage}
                <Form>
                    <Form.Input
                        label="Address"
                        required
                        error={!!state.validationErrors.addressLine}
                        value={state.addressLine1}
                        onChange={onValueChange}
                        stateName="addressLine1"
                    />
                    <Form.Input
                        error={!!state.validationErrors.addressLine}
                        value={state.addressLine2}
                        onChange={onValueChange}
                        stateName="addressLine2"
                    />
                    <Form.Input
                        error={!!state.validationErrors.addressLine}
                        value={state.addressLine3}
                        onChange={onValueChange}
                        stateName="addressLine3"
                    />
                    <Form.Group widths="equal">
                        <Form.Input
                            label="City"
                            required
                            error={!!state.validationErrors.city}
                            value={state.city}
                            onChange={onValueChange}
                            stateName="city"
                        />
                        <Form.Dropdown
                            label="State"
                            required
                            selection
                            search
                            placeholder="Select or enter state"
                            error={!!state.validationErrors.state}
                            allowAdditions
                            additionLabel=""
                            options={stateOptions}
                            value={currentStateDropdownValue}
                            onChange={onValueChange}
                            stateName="state"
                            onAddItem={onStateAddItem}
                        />
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input
                            label="Zip/Postal code"
                            required
                            error={!!state.validationErrors.zip}
                            value={state.zip}
                            onChange={onValueChange}
                            stateName="zip"
                        />
                        <Form.Dropdown
                            label="Country"
                            required
                            selection
                            search
                            placeholder="Select or enter country"
                            error={!!state.validationErrors.country}
                            allowAdditions
                            additionLabel=""
                            options={countryOptions}
                            value={state.country}
                            onChange={onValueChange}
                            stateName="country"
                            onAddItem={onCountryAddItem}
                        />
                    </Form.Group>
                    <Form.Input
                        label="Telephone"
                        required
                        error={!!state.validationErrors.telephone}
                        value={state.telephone}
                        onChange={onValueChange}
                        stateName="telephone"
                    />
                    <Form.Input
                        label="Fax"
                        error={!!state.validationErrors.fax}
                        value={state.fax}
                        onChange={onValueChange}
                        stateName="fax"
                    />
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    content={submitButtonLabel}
                    primary
                    onClick={onSubmitClick}
                />
                <Button content="Close" onClick={props.onModalClose} />
            </Modal.Actions>

            <Dimmer active={state.loading}>
                <Loader content="Loading..." size="huge" />
            </Dimmer>
        </Modal>
    );
}
